import { useEffect, useMemo, useRef, useState } from "react";
import type { Asesor } from "@/lib/api";
import ListaAsesores from "./ListaAsesores";
import { useAsesores } from "./useAsesores";

type Props = {
  /** ID del asesor actualmente asignado (null = sin asesor). */
  value: number | null;
  /** Callback al seleccionar o limpiar (null = desasignar). */
  onChange: (id: number | null) => void;
  /** Muestra fila "Quitar asesor" al fondo del dropdown (default true). */
  allowClear?: boolean;
  /**
   * Si true, el picker monta ya en modo edición (dropdown abierto).
   * Úsalo cuando se llega por deep-link desde el detalle del caso.
   */
  autoOpen?: boolean;
};

/**
 * Selector de asesor comercial con dos modos:
 *
 * - **Lectura**: muestra el nombre del asesor como texto plano + botón ✎
 *   para editar, o un botón ghost "+ Asignar asesor" si no hay ninguno.
 * - **Edición** (click en ✎ / "+ Asignar"): despliega un dropdown con
 *   campo de búsqueda y lista (`ListaAsesores`).
 *
 * Auto-guarda al seleccionar (llama onChange). Colapsa a lectura con
 * Escape o click fuera — sin guardar si no se eligió nada.
 *
 * Para cambiar el asesor **desde el caso** se usa `AsignarAsesorModal`, que
 * comparte la misma lista pero en un modal (ahí un dropdown flotante quedaría
 * cortado por el scroll del panel).
 */
export default function AsesorPicker({
  value,
  onChange,
  allowClear = true,
  autoOpen = false,
}: Props) {
  const { asesores, cargando } = useAsesores();
  const [open, setOpen] = useState(autoOpen);

  const containerRef = useRef<HTMLDivElement>(null);

  // Cerrar al hacer click fuera
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const seleccionado = useMemo(
    () => asesores.find((a) => a.id === value) ?? null,
    [asesores, value],
  );

  function seleccionar(a: Asesor) {
    onChange(a.id);
    setOpen(false);
  }

  function quitar() {
    onChange(null);
    setOpen(false);
  }

  // ── Modo edición ──────────────────────────────────────────────────────────
  if (open) {
    return (
      <div ref={containerRef} className="relative w-full max-w-xs">
        {/* `flotante`: la lista se dibuja encima de lo que sigue para no
            empujar el layout de la tarjeta del cliente mientras se elige. */}
        <ListaAsesores
          flotante
          asesores={asesores}
          cargando={cargando}
          value={value}
          autoFocus
          onSelect={seleccionar}
          onClear={allowClear ? quitar : undefined}
          onEscape={() => setOpen(false)}
        />
      </div>
    );
  }

  // ── Modo lectura ──────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} className="flex items-center gap-1.5 flex-wrap">
      {seleccionado ? (
        <>
          <span className="text-xs text-zinc-200">{seleccionado.nombre}</span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Cambiar asesor"
            className="text-zinc-500 hover:text-zinc-200 transition text-xs"
          >
            ✎
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="btn-ghost text-xs"
        >
          + Asignar asesor
        </button>
      )}
    </div>
  );
}
